import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard, LoadingBlock, EmptyState, Card } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { formatDate, REQUEST_STATUS_LABELS, BOOKING_STATUS_LABELS } from "@/lib/format";
import { PlusCircle, ClipboardList, CalendarCheck, Clock, ArrowRight, MapPin, UserRound } from "lucide-react";

export default function PatientDashboard() {
  const { user } = useAuth();
  const { data: reqData, isLoading: l1 } = useQuery({ queryKey: ["requests"], queryFn: async () => (await api.get("/requests")).data });
  const { data: bookData, isLoading: l2 } = useQuery({ queryKey: ["bookings"], queryFn: async () => (await api.get("/bookings")).data });

  const requests = reqData?.requests || [];
  const bookings = bookData?.bookings || [];
  const lastReq = requests[0];
  const upcoming = bookings
    .filter((b) => ["confermata", "in_attesa_di_conferma"].includes(b.booking_status))
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0];

  return (
    <DashboardLayout title="Dashboard">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#102A2A]">Benvenuto, {user?.first_name}! 👋</h2>
        <p className="text-[#5C6B68] mt-1">Gestisci le tue richieste e prenotazioni di assistenza a domicilio.</p>
      </div>

      <div className="bg-[#087A73] rounded-2xl p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-xl font-bold text-white">Hai bisogno di una prestazione?</h3>
          <p className="text-[#EAF3EF] mt-1">Richiedi un professionista verificato in pochi minuti.</p>
        </div>
        <Link to="/paziente/nuova-richiesta">
          <Button data-testid="dash-nuova-richiesta" className="bg-white text-[#087A73] hover:bg-[#EAF3EF] rounded-xl h-13 min-h-[52px] px-6 font-bold">
            <PlusCircle className="mr-2 h-5 w-5" /> Richiedi una prestazione
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <StatCard icon={ClipboardList} label="Richieste totali" value={requests.length} testid="stat-requests" />
        <StatCard icon={CalendarCheck} label="Prenotazioni" value={bookings.length} tone="dark" testid="stat-bookings" />
        <StatCard icon={Clock} label="In corso" value={bookings.filter((b) => ["confermata", "in_attesa_di_conferma"].includes(b.booking_status)).length} tone="yellow" testid="stat-active" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#102A2A]">Ultima richiesta</h3>
            <Link to="/paziente/richieste" className="text-sm text-[#087A73] font-semibold flex items-center gap-1">Tutte <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {l1 ? <LoadingBlock /> : lastReq ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="font-semibold text-[#102A2A]">{lastReq.service_name}</span><StatusBadge status={lastReq.status} label={REQUEST_STATUS_LABELS[lastReq.status]} /></div>
              <p className="text-sm text-[#5C6B68] flex items-center gap-1"><MapPin className="h-4 w-4" /> {lastReq.municipality} · {formatDate(lastReq.preferred_date)}</p>
            </div>
          ) : <p className="text-sm text-[#5C6B68]">Nessuna richiesta ancora.</p>}
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#102A2A]">Prossima visita</h3>
            <Link to="/paziente/prenotazioni" className="text-sm text-[#087A73] font-semibold flex items-center gap-1">Tutte <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {l2 ? <LoadingBlock /> : upcoming ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between"><span className="font-semibold text-[#102A2A]">{upcoming.service_name}</span><StatusBadge status={upcoming.booking_status} label={BOOKING_STATUS_LABELS[upcoming.booking_status]} /></div>
              <p className="text-sm text-[#5C6B68] flex items-center gap-1"><UserRound className="h-4 w-4" /> {upcoming.professional_name}</p>
              <p className="text-sm text-[#5C6B68] flex items-center gap-1"><CalendarCheck className="h-4 w-4" /> {formatDate(upcoming.scheduled_date)} · {upcoming.start_time}</p>
            </div>
          ) : <p className="text-sm text-[#5C6B68]">Nessuna visita in programma.</p>}
        </Card>
      </div>

      <div className="mt-6">
        <h3 className="font-bold text-[#102A2A] mb-4">Storico prenotazioni</h3>
        {l2 ? <LoadingBlock /> : bookings.length === 0 ? (
          <EmptyState title="Nessuna prenotazione" subtitle="Le tue prenotazioni appariranno qui."
            action={<Link to="/paziente/nuova-richiesta"><Button className="bg-[#087A73] text-white rounded-xl h-12 px-6">Nuova richiesta</Button></Link>} />
        ) : (
          <div className="space-y-3">
            {bookings.slice(0, 5).map((b) => (
              <Card key={b.id} className="flex items-center justify-between p-4">
                <div><p className="font-semibold text-[#102A2A]">{b.service_name}</p><p className="text-sm text-[#5C6B68]">{formatDate(b.scheduled_date)} · {b.professional_name}</p></div>
                <StatusBadge status={b.booking_status} label={BOOKING_STATUS_LABELS[b.booking_status]} />
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
